import { ATTR_NAME } from 'constants/attr-name'
import { CLASS_NAME } from 'constants/class-names'
import { ApiService } from 'services/api/api.service'
import { NativeComponent } from 'components/main/ads/native.component'
import {
    renderGlobeNewsWireWidget,
    LIST_WIDGET_NOT_ENOUGH_ITEM,
    LIST_WIDGET_NO_ITEMS
} from './templates/globe-newswire'
import { strToBool } from 'utils/string'
import { Article } from 'components/main/article/article'
import { AbstractObserver } from 'utils/abstract-classes/abstract-observer'
import { ListType } from 'constants/enums'
import { IListItems, IItem, IList } from './list-interface'
import { logger } from 'utils/system/logger'
import { Nullable } from 'constants/types'

const EMPTY_LIST_ID = '00000000-0000-0000-0000-000000000000'
const LIST_WIDGET_NO_CONTENT_HEADER = 'No content available yet!'
const LIST_WIDGET_NO_CONTENT_MESSAGE =
    'Come back often to see if we have new content.'

export enum ListRender {
    CATEGORY = 'category',
    WIDGET = 'widget',
    CAROUSEL = 'carousel',
    PARTNER_FEED = 'partner_feed'
}

export class ListComponent extends AbstractObserver implements IList {
    public static defaultType = ListType.CATEGORY
    public static defaultRender = ListRender.CATEGORY
    public static defaultListId = EMPTY_LIST_ID

    public id: string
    public name: string
    public url: string
    public type: ListType
    public listRender?: ListRender
    public pos?: number
    public catSlug?: string
    public slugsAccepted?: string
    public slugsExcluded?: string

    public nativeAdEnabled?: boolean
    public nativeAdCounter?: string

    // eslint-disable-next-line no-magic-numbers
    public static readonly PARTNER_FEED_HEADLINES = 2
    // eslint-disable-next-line no-magic-numbers
    public static readonly PARTNER_FEED_SECTION_ARTICLE_NUMBER = 4

    private originUrls: boolean
    private contentId: string
    private listFrom: number
    private maxItems: number
    private minItems: number
    private removeDuplication: boolean
    private isSponsored: boolean
    private listClass: string

    private apiService: ApiService

    /**
     * list constructor.
     *
     * @param {Element} el
     */
    public constructor(protected el: HTMLElement) {
        super(el)
        this.activateObserver()
    }

    /**
     * Initializes the list.
     *
     * @param {Element} el
     */
    public render(): void {
        this.id =
            this.el.getAttribute(ATTR_NAME.main.list.id) ||
            ListComponent.defaultListId
        this.name = this.el.getAttribute(ATTR_NAME.main.list.name) || ''
        this.catSlug = this.el.getAttribute(ATTR_NAME.main.list.catSlug) || ''
        this.slugsAccepted =
            this.el.getAttribute(ATTR_NAME.main.list.slugsAccepted) || ''
        this.slugsExcluded =
            this.el.getAttribute(ATTR_NAME.main.list.slugsExcluded) || ''
        this.url = this.el.getAttribute(ATTR_NAME.main.list.url) || ''
        this.originUrls = strToBool(
            this.el.getAttribute(ATTR_NAME.main.list.originUrls)
        )
        this.contentId =
            this.el.getAttribute(ATTR_NAME.main.list.contentId) || ''
        this.listFrom = parseInt(
            this.el.getAttribute(ATTR_NAME.main.list.listFrom) || '0'
        )
        this.type =
            (this.el.getAttribute(ATTR_NAME.main.list.type) as ListType) ||
            ListComponent.defaultType
        this.nativeAdEnabled = strToBool(
            this.el.getAttribute(ATTR_NAME.main.list.nativeAdEnabled),
            false
        )
        this.nativeAdCounter =
            this.el.getAttribute(ATTR_NAME.main.list.nativeAdCounter) || '0'
        this.maxItems =
            parseInt(this.el.getAttribute(ATTR_NAME.main.list.maxItem) || '5') +
            (this.nativeAdEnabled ? 1 : 0)
        this.minItems = parseInt(
            this.el.getAttribute(ATTR_NAME.main.list.minItem) || '1'
        )
        this.removeDuplication = strToBool(
            this.el.getAttribute(ATTR_NAME.main.list.removeDuplication),
            true
        )
        this.isSponsored = strToBool(
            this.el.getAttribute(ATTR_NAME.main.list.isSponsored),
            false
        )
        this.listRender =
            (this.el.getAttribute(ATTR_NAME.main.list.render) as ListRender) ||
            ListComponent.defaultRender
        this.pos = parseInt(
            this.el.getAttribute(ATTR_NAME.main.list.pos) || '0'
        )
        this.listClass =
            this.el.getAttribute(ATTR_NAME.main.list.listClass) || ''

        this.apiService = new ApiService()

        this.loadList()
    }

    /**
     * Load the list content, only once
     */
    public loadList(): void {
        const id =
            this.type === ListType.RELATED_ARTICLES && this.contentId
                ? this.contentId
                : this.id

        switch (this.listRender) {
            case ListRender.PARTNER_FEED:
            case ListRender.CATEGORY:
            case ListRender.CAROUSEL:
                // if the render type is category, load the html from the server
                this.fetchArticlesByHtml(id, this.getTemplateName())
                break
            case ListRender.WIDGET: {
                if (this.type == ListType.GLOBE_NEWSWIRE) {
                    // if the list type is globe newswire, load by json
                    this.fetchArticlesByJson(id)
                } else {
                    // otherwise, load the html from the server
                    // this includes trending or category (ie. businesswire, special features etc.)
                    this.fetchArticlesByHtml(id, this.getTemplateName())
                }
                break
            }
            default:
                // other render types should load using json
                this.fetchArticlesByJson(id)
                break
        }
    }

    /**
     * Fetch the articles from the server, receiving full html templates for the lists
     */
    public fetchArticlesByHtml(id: string, template?: string): void {
        this.apiService
            .fetchListHtml(
                id,
                this.catSlug || this.name,
                this.type,
                this.listFrom,
                this.originUrls,
                this.isSponsored,
                this.slugsAccepted,
                this.slugsExcluded,
                template,
                this.nativeAdEnabled,
                this.nativeAdCounter
            )
            .then((articleElements: Article[]) => {
                let articles = articleElements

                // if it has an event trigger on it
                let event: Nullable<string> = ''
                let eventType: Nullable<string> = ''
                let eventValue: Nullable<string> = ''
                if (
                    articles.length &&
                    this.getElement('[data-first-item]', articles[0].el)
                ) {
                    const eventElement = this.getElement(
                        '[data-first-item]',
                        articles[0].el
                    )
                    event = eventElement?.getAttribute('data-evt') || ''
                    eventType = eventElement?.getAttribute('data-evt-typ') || ''
                    eventValue =
                        eventElement?.getAttribute('data-evt-val') || ''
                    eventElement?.removeAttribute('data-evt')
                    eventElement?.removeAttribute('data-evt-typ')
                    eventElement?.removeAttribute('data-evt-val')
                    eventElement?.removeAttribute('data-async')
                    eventElement?.removeAttribute('data-first-item')
                }

                // filter out the current article from the list
                articles = this.filterCurrentArticle(articles)

                if (this.removeDuplication) {
                    // filter out any duplicates from the page
                    articles = this.filterDuplicatedArticle(articles)
                }

                // slice the articles list to the number required
                articles = articles.slice(0, this.maxItems)

                // when the number of article is less than requirement, exit
                if (articles.length < this.minItems) {
                    this.actionAfterFetch([])
                    return
                }

                // Replace ad position
                const ad = articles.filter((listItem) =>
                    this.getElement('.ad', listItem.el)
                )
                articles = articles.filter(
                    (listItem) => !this.getElement('.ad', listItem.el)
                )
                if (!articles) {
                    this.actionAfterFetch([])
                    return
                }
                if (ad.length) {
                    if (articles.length > 1) {
                        articles.splice(2, 0, ad[0])
                    }
                    if (articles.length === 1) {
                        articles.splice(1, 0, ad[0])
                    }
                }

                const articleCount = articles.filter((item) =>
                    item.el.classList.contains('article-card')
                ).length
                if (event && articleCount) {
                    eventValue = eventValue.replace(
                        /"items_count": \d+/,
                        `"items_count": ${articleCount}`
                    )
                    const firstItem = this.getElement('div', articles[0].el)
                    firstItem?.setAttribute('data-async', '')
                    firstItem?.setAttribute('data-evt', event)
                    firstItem?.setAttribute('data-evt-typ', eventType)
                    firstItem?.setAttribute('data-evt-val', eventValue)
                }

                const outerStrings = this.getArticlesHtmlStrings(id, articles)

                // Wrapper and joint per type and render
                if (this.listRender == ListRender.PARTNER_FEED) {
                    this.appendPartnerFeedInnerHtml(outerStrings)
                } else {
                    this.appendAsListHtml(outerStrings)
                }

                this.showHiddenTitle()
                this.actionAfterFetch(articles)

                if (ad.length) {
                    const adHtml = this.getElement('[data-ad]', this.el)
                    if (adHtml) {
                        // call FEM to fill the ad slot via event listener
                        dispatchEvent(
                            new CustomEvent('adSlotAdded', {
                                detail: { el: [adHtml] }
                            })
                        )
                    }
                }
            })
            .then(() => {
                // Should be `finally` but broken in IE11 with FEM
                this.el.classList.remove(CLASS_NAME.list.listLoading)
            })
            .catch((err) => {
                // Should be `finally` but broken in IE11 with FEM
                this.el.classList.remove(CLASS_NAME.list.listLoading)
                logger('error', err)
            })
    }

    /**
     * Set analytics value into each article
     * @param {string} id List ID
     * @param {Article[]} articles
     * @return {string[]}
     */
    private getArticlesHtmlStrings(id: string, articles: Article[]): string[] {
        return articles.map((article: Article, index: number) => {
            // update analytics values
            article.setAnalyticsValue('list.id', id)
            article.setAnalyticsValue(
                'list.type',
                `${this.type} ${this.listRender}`
            )
            article.setAnalyticsValue('list.name', this.name)
            article.setAnalyticsValue('list.position', index.toString())
            article.setAnalyticsValue('list.storyposition', index.toString())
            article.setAnalyticsValue('list.url', this.url)
            this.name = this.name || 'widget'
            article.setAnalyticsValue(
                'list.widgetname',
                `${this.name.replace(/\s+/g, '-').toLowerCase()}-${
                    this.listRender
                }`
            )
            return article.getOuterHTML()
        })
    }

    /**
     * The follow-up actions after fetch HTML
     * @param {Article[]} articles
     */
    private actionAfterFetch(articles: Article[]): string {
        switch (this.listRender) {
            case ListRender.PARTNER_FEED:
                return ListRender.PARTNER_FEED
            case ListRender.CAROUSEL: {
                if (articles.length == 0) {
                    this.removeWidgetParent()
                }
                return ListRender.CAROUSEL
            }
            case ListRender.WIDGET: {
                if (articles.length == 0) {
                    this.addNoContentMessage()
                }
                if (this.nativeAdEnabled && articles.length > 0) {
                    this.registerNativeAds()
                }
                return ListRender.WIDGET
            }
            case ListRender.CATEGORY:
            default: {
                if (articles.length > 0) {
                    this.showMore()
                }

                // native ads are inserted for non-widget lists
                this.registerNativeAds()
                return 'default'
            }
        }
    }

    /**
     * Append articles into this el as semantic list <li>
     * @param {Article[]} articles
     */
    public appendAsListHtml(articles: string[]): void {
        this.el.innerHTML = ''
        articles.forEach((html: string) => {
            const li = document.createElement('li')
            li.innerHTML = html
            if (this.listClass) {
                li.setAttribute('class', this.listClass)
            }
            if (this.listRender === ListRender.CAROUSEL) {
                li.setAttribute(ATTR_NAME.main.carousel.slideItem, '')
            }
            this.el.append(li)
        })
    }

    /**
     * Append partner feed articles into this el
     * @param {Article[]} articles
     */
    public appendPartnerFeedInnerHtml(articles: string[]): void {
        // ancestor
        const row = document.createElement('div')
        row.classList.add('row')

        // the first two items
        const headlines = document.createElement('div')
        headlines.classList.add(
            'col-xs-12',
            'col-lg-7',
            'divider--right--lg-up',
            CLASS_NAME.list.contentFeatured
        )
        headlines.innerHTML = articles
            .slice(0, ListComponent.PARTNER_FEED_HEADLINES)
            .join('')
        row.append(headlines)

        // section ancestor
        const sections = document.createElement('div')
        sections.classList.add(
            'col-xs-12',
            'col-lg-5',
            CLASS_NAME.list.contentMinimized
        )
        const sectionHeading = document.createElement('div')
        sectionHeading.classList.add(CLASS_NAME.list.contentSubheading)
        sectionHeading.textContent = 'Briefing'
        sections.append(sectionHeading)

        // section wrapper
        const innerSection = document.createElement('div')
        innerSection.classList.add('col-1fr-1fr--sm-up')

        // push first section articles (4) into the wrapper
        const firstSectionArticles = articles.slice(
            ListComponent.PARTNER_FEED_HEADLINES,
            ListComponent.PARTNER_FEED_HEADLINES +
                ListComponent.PARTNER_FEED_SECTION_ARTICLE_NUMBER
        )
        const firstSection = document.createElement('div')
        firstSection.classList.add(CLASS_NAME.list.contentCol)
        firstSection.innerHTML = firstSectionArticles.join('')
        innerSection.appendChild(firstSection)

        // push second section articles (rest of it) into the wrapper
        const secondSectionArticles = articles.slice(
            ListComponent.PARTNER_FEED_HEADLINES +
                ListComponent.PARTNER_FEED_SECTION_ARTICLE_NUMBER
        )
        const secondSection = document.createElement('div')
        secondSection.classList.add(CLASS_NAME.list.contentCol)
        secondSection.innerHTML = secondSectionArticles.join('')
        innerSection.appendChild(secondSection)

        // append all
        sections.append(innerSection)
        row.append(sections)
        this.el.innerHTML = ''
        this.el.append(row)
    }

    /**
     * Fetch the articles from the server, receiving json data for the lists
     * @deprecated - only globenewswire is using
     */
    public fetchArticlesByJson(id: string): void {
        this.apiService
            .fetchList(id, this.name, this.type, this.listFrom)
            .then((listItems: IListItems) => {
                listItems.items = this.filterDuplicatedItem(listItems.items)

                if (
                    !(
                        listItems.errors === undefined ||
                        listItems.errors.length === 0
                    )
                ) {
                    logger(
                        'error',
                        `fetchList error for list: ${id}`,
                        listItems.errors
                    )
                }
                if (this.contentId) {
                    listItems.items = listItems.items.filter(
                        (item) => item._id !== this.contentId
                    )
                }
                switch (this.listRender) {
                    case ListRender.WIDGET: {
                        const list = renderGlobeNewsWireWidget(
                            listItems,
                            this.maxItems,
                            this.minItems
                        )

                        if (list === LIST_WIDGET_NOT_ENOUGH_ITEM) {
                            this.removeWidgetParent()
                            break
                        }
                        this.el.innerHTML = list
                        break
                    }
                }
            })
            .then(() => {
                // Should be `finally` but broken in IE11 with FEM
                this.el.classList.remove(CLASS_NAME.list.listLoading)
            })
            .catch((err) => {
                // Should be `finally` but broken in IE11 with FEM
                this.el.classList.remove(CLASS_NAME.list.listLoading)
                logger('error', err)
            })
    }

    /**
     * Filter out the current article from the articles list
     */
    public filterCurrentArticle(articles: Article[]): Article[] {
        let articleList = articles
        if (this.contentId) {
            articleList = articleList.filter(
                (item: Article) => item.id !== this.contentId
            )
        }
        return articleList
    }

    /**
     * Filter article out which duplicated data-article-id from page.
     */
    public filterDuplicatedArticle(articles: Article[]): Article[] {
        const elements = this.getElements(
            `[${ATTR_NAME.main.list.articleId}]:not([${ATTR_NAME.main.list.articleId}=""])`
        )
        if (!elements.length) {
            return articles
        }
        const articleIds = Array.from(elements).reduce(
            (acc, node): string[] => {
                const articleId = node.getAttribute(
                    ATTR_NAME.main.list.articleId
                )
                if (!articleId) {
                    return acc
                }
                return [...acc, articleId]
            },
            []
        )

        // Remove articleId from given articles
        return articles.filter((article: Article) => {
            if (!article.id) {
                return true
            }
            if (-1 !== articleIds.indexOf(article.id)) {
                return false
            }
            return true
        })
    }

    /**
     * Filter item out which duplicated data-article-id from page
     *
     * @deprecated - this should only be used for json based return content.  For html content
     * use filterDuplicatedArticle
     */
    public filterDuplicatedItem(items: IItem[]): IItem[] {
        const elements = this.getElements(
            `[${ATTR_NAME.main.list.articleId}]:not([${ATTR_NAME.main.list.articleId}=""])`
        )
        if (!elements.length) {
            return items
        }
        const articleIds = elements.reduce((acc, node): string[] => {
            return [
                ...acc,
                node.getAttribute(ATTR_NAME.main.list.articleId) || ''
            ]
        }, [])

        // Filter items
        return items.filter((item) => {
            if (!item._id) {
                return true
            }
            if (-1 !== articleIds.indexOf(item._id)) {
                return false
            }
            return true
        })
    }

    /***
     * Show a hidden title for the widget, if there is one
     * @deprecated -- from V1 of story lists
     */
    public showHiddenTitle(): void {
        if (this.el.parentElement) {
            const containers = this.el.parentElement.getElementsByClassName(
                'section-title--page-story'
            )
            if (containers.length > 0) {
                const container = containers[0] as HTMLElement
                container.classList.remove(CLASS_NAME.common.hidden)
            }
        }
    }

    /***
     * Remove the parent element of a widget, if there are not enough articles to display
     */
    public removeWidgetParent(): void {
        const parent =
            this.listRender === ListRender.CAROUSEL
                ? this.el.closest(`.${CLASS_NAME.list.carouselWidget}`)
                : this.el.closest(`.${CLASS_NAME.list.listWidget}`)
        if (!parent || !parent.parentNode) {
            this.el.innerHTML = LIST_WIDGET_NO_ITEMS
        } else {
            parent.parentNode.removeChild(parent)
        }
    }

    /***
     * Add no content section when no content available
     */
    public addNoContentMessage(): void {
        const parent =
            this.listRender === ListRender.CAROUSEL
                ? this.el.closest(`.${CLASS_NAME.list.carouselWidget}`)
                : this.el.closest(`.${CLASS_NAME.list.listWidget}`)
        if (!parent || !parent.parentNode) {
            this.el.innerHTML = LIST_WIDGET_NO_CONTENT_HEADER
        } else {
            parent.removeChild(this.el)
            const messageContainer = document.createElement('div')
            messageContainer.className = `col-xs-12 col-lg-8 offset-lg-2 ${CLASS_NAME.list.listWidgetContentUnavailable}`
            const headerMessage = document.createElement('p')
            const headerMessageStrong = document.createElement('strong')
            headerMessageStrong.innerHTML = LIST_WIDGET_NO_CONTENT_HEADER
            headerMessage.appendChild(headerMessageStrong)
            messageContainer.appendChild(headerMessage)
            const message = document.createElement('p')
            message.innerHTML = LIST_WIDGET_NO_CONTENT_MESSAGE
            messageContainer.appendChild(message)
            parent.appendChild(messageContainer)
        }
    }

    /**
     * Get the template name to use, if an html loader
     */
    public getTemplateName(): string {
        let templateName = this.el.getAttribute(
            ATTR_NAME.main.list.templateName
        )

        if (!templateName || templateName == '' || templateName == 'None') {
            if (this.listRender == ListRender.CATEGORY) {
                templateName = 'feed-card-list'
            } else {
                templateName = 'headline-only-mini-list'
            }
        }

        return templateName
    }

    /**
     * Find all of the native ads in the more section and create components for them so they can
     * be properly positioned
     *
     */
    private registerNativeAds(): void {
        this.getElements(
            `.js-${this.el.id}-native`,
            this.el.parentElement
        ).forEach((nativeSection) => {
            if (nativeSection) {
                this.getElements(
                    `[${ATTR_NAME.native._}]`,
                    nativeSection
                ).forEach((nativeAd: HTMLElement) => {
                    new NativeComponent(nativeAd, this.el)
                })
            }
        })
    }

    /**
     * Display the show more section, if it exists on the page
     */
    private showMore(): void {
        const moreSection = this.getElements(`.js-${this.el.id}-more`)[0]
        if (this.name && this.url && this.listFrom == 0) {
            moreSection.classList.remove(CLASS_NAME.common.visuallyHidden)
        }
    }
}
